/**
 * Unified Storage API
 *
 * Single API surface that conditionally routes between:
 * - Local storage adapters (IndexedDB/FileSystem) for NO_ACCOUNT and ACCOUNT_LOCAL modes
 * - Remote backend API for ACCOUNT_CLOUD mode
 *
 * This solves the dual API problem by providing components with a consistent
 * interface regardless of the underlying storage mechanism (Task 161)
 */

import {
  createApi,
  BaseQueryFn,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { getStorageAdapter } from '@/data/adapters/StorageAdapterFactory';
import { AuthMode, AuthModeManager } from '@/core/services/auth-mode';
import type {
  StorageFile,
  StorageFilter,
  SearchResult,
  StorageError,
  CreateFileRequest,
} from '@/data/adapters/types';

// Get the current auth mode
const getAuthMode = (): AuthMode => {
  try {
    const manager = AuthModeManager.getInstance();
    return manager.getMode();
  } catch (error) {
    // Default to NO_ACCOUNT if manager not initialized
    return AuthMode.NO_ACCOUNT;
  }
};

// Backend API base URL - will be configured based on environment
const BACKEND_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3456';

/**
 * Unified base query that routes based on AuthMode
 */
const unifiedBaseQuery: BaseQueryFn<
  { method: string; args?: any; endpoint?: string; body?: any },
  any,
  StorageError
> = async ({ method, args, body }) => {
  const authMode = getAuthMode();

  try {
    // Route based on auth mode
    if (
      authMode === AuthMode.NO_ACCOUNT ||
      authMode === AuthMode.ACCOUNT_LOCAL
    ) {
      // Use local storage adapter
      const adapter = await getStorageAdapter();

      // Handle special case for FileListResponse
      if (method === 'getFiles') {
        const response = await adapter.getFiles(args?.[0]);
        // Return just the files array for consistency
        const files = Array.isArray(response)
          ? response
          : (response as any).files || [];
        return { data: files };
      }

      // Call the appropriate method on the adapter
      const result = await (adapter as any)[method](...(args || []));
      return { data: result };
    } else if (authMode === AuthMode.ACCOUNT_CLOUD) {
      // Use backend API
      const baseQuery = fetchBaseQuery({
        baseUrl: BACKEND_API_URL,
        prepareHeaders: (headers) => {
          // Add auth token if available
          const token = localStorage.getItem('auth_token');
          if (token) {
            headers.set('authorization', `Bearer ${token}`);
          }
          return headers;
        },
      });

      // Map adapter methods to API endpoints
      const endpointMap: Record<string, { url: string; method: string }> = {
        getFiles: { url: '/api/v1/files', method: 'GET' },
        getFile: { url: `/api/v1/files/${args?.[0]}`, method: 'GET' },
        createFile: { url: '/api/v1/files', method: 'POST' },
        updateFile: { url: `/api/v1/files/${args?.[0]}`, method: 'PUT' },
        deleteFile: { url: `/api/v1/files/${args?.[0]}`, method: 'DELETE' },
        searchFiles: { url: '/api/v1/files/search', method: 'POST' },
        exportData: { url: '/api/v1/export', method: 'GET' },
        importData: { url: '/api/v1/import', method: 'POST' },
      };

      const apiEndpoint = endpointMap[method];
      if (!apiEndpoint) {
        throw new Error(`Unknown API method: ${method}`);
      }

      // Prepare the request based on method type
      let requestBody = body;
      if (method === 'createFile' || method === 'updateFile') {
        requestBody = args?.[method === 'updateFile' ? 1 : 0];
      } else if (method === 'searchFiles') {
        requestBody = { query: args?.[0], options: args?.[1] };
      }

      const result = await baseQuery(
        {
          url: apiEndpoint.url,
          method: apiEndpoint.method,
          body: requestBody,
          params: method === 'getFiles' ? args?.[0] : undefined,
        },
        {
          signal: new AbortController().signal,
          abort: () => {},
          dispatch: {} as any,
          getState: {} as any,
          extra: {},
          endpoint: '',
          type: 'query',
        },
        {}
      );

      if (result.error) {
        return {
          error: {
            name: 'APIError',
            message:
              (result.error.data as any)?.message || 'API request failed',
            code: 'NETWORK_ERROR',
            retryable: true,
            fallbackAvailable: true,
          } as StorageError,
        };
      }

      return { data: result.data };
    }

    // Unknown auth mode
    throw new Error(`Unknown auth mode: ${authMode}`);
  } catch (error) {
    const storageError = error as StorageError;
    return {
      error: {
        name: storageError.name || 'StorageError',
        message: storageError.message || 'An error occurred',
        code: storageError.code || 'NETWORK_ERROR',
        retryable: storageError.retryable ?? false,
        fallbackAvailable: storageError.fallbackAvailable ?? false,
      },
    };
  }
};

/**
 * Unified Storage API with intelligent routing
 */
export const unifiedStorageApi = createApi({
  reducerPath: 'unifiedStorageApi',
  baseQuery: unifiedBaseQuery,
  tagTypes: ['File', 'Files', 'Search', 'Permission'],
  endpoints: (builder) => ({
    // Get all files
    getFiles: builder.query<StorageFile[], StorageFilter | undefined>({
      query: (filter) => ({
        method: 'getFiles',
        args: [filter],
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'File' as const, id })),
              { type: 'Files', id: 'LIST' },
            ]
          : [{ type: 'Files', id: 'LIST' }],
    }),

    // Get single file
    getFile: builder.query<StorageFile, string>({
      query: (id) => ({
        method: 'getFile',
        args: [id],
      }),
      providesTags: (_result, _error, id) => [{ type: 'File', id }],
    }),

    // Create file with optimistic update
    createFile: builder.mutation<StorageFile, CreateFileRequest>({
      query: (file) => ({
        method: 'createFile',
        args: [file],
      }),
      // Optimistic update
      onQueryStarted: async (file, { dispatch, queryFulfilled, getState }) => {
        // Get the current project ID from state
        const state = getState() as any;
        const projectId = state.project?.currentProject?.id;
        const queryArg = projectId ? { projectId } : undefined;

        // Create optimistic file
        const optimisticFile: StorageFile = {
          ...file,
          id: `temp-${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Update cache optimistically
        const patchResult = dispatch(
          unifiedStorageApi.util.updateQueryData(
            'getFiles',
            queryArg,
            (draft) => {
              draft.push(optimisticFile);
            }
          )
        );

        try {
          const { data: createdFile } = await queryFulfilled;
          // Replace optimistic file with real one
          dispatch(
            unifiedStorageApi.util.updateQueryData(
              'getFiles',
              queryArg,
              (draft) => {
                const index = draft.findIndex(
                  (f) => f.id === optimisticFile.id
                );
                if (index !== -1) {
                  draft[index] = createdFile;
                }
              }
            )
          );
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: 'Files', id: 'LIST' }],
    }),

    // Update file with optimistic update
    updateFile: builder.mutation<
      StorageFile,
      { id: string; updates: Partial<StorageFile> }
    >({
      query: ({ id, updates }) => ({
        method: 'updateFile',
        args: [id, updates],
      }),
      // Optimistic update
      onQueryStarted: async (
        { id, updates },
        { dispatch, queryFulfilled, getState }
      ) => {
        // Get the current project ID from state
        const state = getState() as any;
        const projectId = state.project?.currentProject?.id;
        const queryArg = projectId ? { projectId } : undefined;

        const patchResult = dispatch(
          unifiedStorageApi.util.updateQueryData(
            'getFiles',
            queryArg,
            (draft) => {
              const file = draft.find((f) => f.id === id);
              if (file) {
                Object.assign(file, updates, {
                  updatedAt: new Date().toISOString(),
                });
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'File', id },
        { type: 'Files', id: 'LIST' },
      ],
    }),

    // Delete file with optimistic update
    deleteFile: builder.mutation<void, string>({
      query: (id) => ({
        method: 'deleteFile',
        args: [id],
      }),
      // Optimistic update
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        // Get the current project ID from state
        const state = getState() as any;
        const projectId = state.project?.currentProject?.id;
        const queryArg = projectId ? { projectId } : undefined;

        const patchResult = dispatch(
          unifiedStorageApi.util.updateQueryData(
            'getFiles',
            queryArg,
            (draft) => {
              return draft.filter((f) => f.id !== id);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, id) => [
        { type: 'File', id },
        { type: 'Files', id: 'LIST' },
      ],
    }),

    // Search files
    searchFiles: builder.query<
      SearchResult[],
      { query: string; options?: { limit?: number; offset?: number } }
    >({
      query: ({ query, options }) => ({
        method: 'search',
        args: [query, options],
      }),
      providesTags: [{ type: 'Search', id: 'LIST' }],
    }),

    // Check permission
    hasPermission: builder.query<boolean, void>({
      query: () => ({
        method: 'hasPermission',
        args: [],
      }),
      providesTags: [{ type: 'Permission', id: 'STATUS' }],
    }),

    // Request permission
    requestPermission: builder.mutation<boolean, void>({
      query: () => ({
        method: 'requestPermission',
        args: [],
      }),
      invalidatesTags: [{ type: 'Permission', id: 'STATUS' }],
    }),

    // Export data
    exportData: builder.query<Blob, void>({
      query: () => ({
        method: 'exportData',
        args: [],
      }),
    }),

    // Import data
    importData: builder.mutation<void, Blob>({
      query: (data) => ({
        method: 'importData',
        args: [data],
      }),
      invalidatesTags: [
        { type: 'Files', id: 'LIST' },
        { type: 'Search', id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks for components to use
export const {
  useGetFilesQuery,
  useGetFileQuery,
  useCreateFileMutation,
  useUpdateFileMutation,
  useDeleteFileMutation,
  useSearchFilesQuery,
  useRequestPermissionMutation,
  useHasPermissionQuery,
  useExportDataQuery,
  useImportDataMutation,
} = unifiedStorageApi;

// Export endpoints for use in thunks
export const {
  endpoints: {
    getFiles,
    getFile,
    createFile,
    updateFile,
    deleteFile,
    searchFiles,
    hasPermission,
    requestPermission,
    exportData,
    importData,
  },
} = unifiedStorageApi;

// Re-export for backwards compatibility (gradually migrate components)
export { unifiedStorageApi as storageApi };
